// "The storm of whispering thoughts": whispers circling → the red-blue whirlpool → press to still → silence and sunlight.
export const stormWhispers = {
  slug: "the-storm-of-whispering-thoughts",
  duration: 30,
  stages: [
    { id: "whispers", at: 0, hint: "listen" },
    { id: "whirlpool", at: 7, hint: "" },
    { id: "still", at: 15, hint: "press to still your mind" },
    { id: "silence", at: 17, hint: "breathe" },
  ],
  hold: { at: 16, need: 4, auto: 20 },
  line: { text: "When my hands touched the canvas, my mind fell quiet.", from: 19.5, to: 29 },
  words: { list: ["what if…", "is it… or is it not", "to be, or not to be", "what if…", "is it…", "or is it not", "to be…", "what if…"], from: 0.8, to: 16.5 },
  touch: { kind: "press", from: 15, until: 17 },
  // painted panorama: the red-blue whirlpool of thoughts, calming into light
  layers: [
    { src: "/arcs/storm/whirl.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, flicker: 0.08, shimmer: 0.3, opacity: [[0, 1], [15, 0.8], [17, 0.8], [20, 0.35]], show: [-1, 0, 99, 99] },
  ],
  ambient: { scene: "whispers", until: 17 },
  pulse: { from: 7, to: 16.5, every: 1.15 },
  look: { start: 0.06, lift: [0, 1], end: 0.06 },
  calm: { duration: 12, fade: [1.5, 6], line: [5, 11.5] },
  speed: [[0, 0.25], [7, 0.35], [12, 1.6], [16, 1.8], [19, 0.08], [30, 0.03]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface, uStill, uPulse;
uniform vec4 uMark[8];
varying vec3 vPos;
${"__NOISE__"}
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
vec2 rot(vec2 p, float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c) * p; }
void main(){
  float whirl = smoothstep(6.5, 11.0, uT) * (1.0 - 0.8 * uStill);
  float silence = smoothstep(16.8, 20.5, uT);
  float sun = smoothstep(19.0, 22.0, uT);
  float n = snoise(vec3(vPos * 0.35 + vec3(0.0, uT * 0.03, 0.0))) * 0.5 + 0.5;
  // circular strokes: around the visitor on the walls, a vortex on floor and ceiling
  float th, r;
  vec2 local;
  if (uSurface < 0.5) {
    th = atan(vPos.x, -vPos.z);
    r = vPos.y * 0.9;
    local = vec2(th * uRadius, vPos.y - uCy);
  } else {
    th = atan(vPos.x, -vPos.z);
    r = length(vPos.xz);
    local = rot(vPos.xz, uPhase * 0.05);
  }
  float tight = mix(3.0, 9.0, smoothstep(7.0, 15.0, uT));
  float sp = sin(r * tight + th * 3.0 - uPhase * 2.2 + n * 2.0);
  float sp2 = sin(r * tight * 1.7 - th * 5.0 + uPhase * 1.3 + n * 3.0);
  float ink = smoothstep(0.55, 0.95, sp) ;
  float ink2 = smoothstep(0.65, 0.98, sp2);
  vec3 red = vec3(0.78, 0.12, 0.14), blue = vec3(0.12, 0.24, 0.62), mesh = vec3(0.62, 0.55, 0.6);
  vec3 dusk = mix(vec3(0.09, 0.08, 0.13), vec3(0.16, 0.13, 0.19), n);
  vec3 swirl = dusk;
  swirl = mix(swirl, blue, ink * (0.25 + 0.75 * whirl));
  swirl = mix(swirl, red, ink2 * (0.2 + 0.8 * whirl));
  swirl = mix(swirl, mesh, ink * ink2 * 0.6);
  // criss-crossing strokes and pressed circles laid over the whirl
  vec2 wq = uSurface < 0.5 ? vec2(th * uRadius, vPos.y) : vPos.xz;
  float x1 = 1.0 - smoothstep(0.02, 0.07, abs(sin((wq.x + wq.y) * 2.3 + uPhase * 0.9 + n * 2.5)));
  float x2 = 1.0 - smoothstep(0.02, 0.07, abs(sin((wq.x - wq.y) * 2.7 - uPhase * 1.1 + n * 2.0)));
  swirl = mix(swirl, red * 1.1, x1 * 0.55 * (0.3 + 0.7 * whirl));
  swirl = mix(swirl, blue * 1.2, x2 * 0.55 * (0.3 + 0.7 * whirl));
  vec2 cell = floor(wq / 1.6);
  vec2 cc = (cell + 0.5 + 0.3 * vec2(sin(cell.y * 3.1 + uPhase * 0.3), cos(cell.x * 2.3 - uPhase * 0.2))) * 1.6;
  float dr = length(wq - cc);
  float rings = (1.0 - smoothstep(0.02, 0.05, abs(fract(dr * 3.2 - uPhase * 0.4) - 0.5) * 0.3)) * (1.0 - smoothstep(0.35, 0.75, dr));
  swirl = mix(swirl, mix(red, blue, step(0.5, fract((cell.x + cell.y) * 0.5))) * 1.15, rings * 0.5 * (0.35 + 0.65 * whirl));
  swirl *= 0.85 + 0.3 * uPulse * whirl;
  // thumbprints where she pressed: looping ridges that spiral in
  float print = 0.0;
  for (int i = 0; i < 8; i++){
    vec4 mk = uMark[i];
    if (mk.w <= 0.0) continue;
    vec3 d3 = vPos - mk.xyz;
    float d = length(d3);
    float a = atan(d3.y + d3.z * 0.5, d3.x + d3.z * 0.5);
    float ridge = 0.5 + 0.5 * sin(d * 95.0 + a * 1.0 + sin(a * 2.0) * 1.5);
    float age = uT - mk.w;
    print = max(print, ridge * (1.0 - smoothstep(0.22, 0.42, d)) * smoothstep(0.0, 0.25, age));
  }
  swirl = mix(swirl, mix(red, blue, step(0.5, fract(n * 3.0))) * 1.25 + 0.08, print * 0.75 * (1.0 - silence * 0.6));
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float settle = smoothstep(0.0, 1.0, silence);
  vec3 col = mix(swirl, paint, settle);
  // slow sunlight sweeping across, like her drawing room in the afternoon
  float sx = (uSurface < 0.5 ? th : atan(vPos.x, -vPos.z)) - mix(-2.2, 2.6, smoothstep(19.0, 30.0, uT));
  float band = exp(-pow((sx + (uSurface < 0.5 ? vPos.y * 0.12 : 0.0)) * 1.6, 2.0));
  col += vec3(1.0, 0.82, 0.55) * band * sun * 0.32;
  col = mix(col, col * vec3(1.06, 1.0, 0.9), sun * 0.4);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
